import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

interface UnifiedAnalyticsConfig {
	cloudId?: string;
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	hasThirdPartySection?: boolean;
	productSwitcher?: string;
}

interface UnifiedAnalyticsContextProviderProps {
	children: ReactNode;
	cloudId?: string;
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	hasThirdPartySection?: boolean;
	productSwitcher?: string;
}

const UnifiedAnalyticsContext = createContext<UnifiedAnalyticsConfig | undefined>(undefined);

export const UnifiedAnalyticsContextProvider = ({
	productSwitcher = 'appSwitcher',
	cloudId = 'unknown',
	hasSwitcherAnalytics = false,
	hasThirdPartySection = false,
	hasDiscoverySection = false,
	children,
}: UnifiedAnalyticsContextProviderProps): React.JSX.Element => {
	const contextValue = useMemo<UnifiedAnalyticsConfig>(
		() => ({
			productSwitcher,
			cloudId,
			hasSwitcherAnalytics,
			hasThirdPartySection,
			hasDiscoverySection,
		}),
		[productSwitcher, cloudId, hasSwitcherAnalytics, hasThirdPartySection, hasDiscoverySection],
	);

	return (
		<UnifiedAnalyticsContext.Provider value={contextValue}>
			{children}
		</UnifiedAnalyticsContext.Provider>
	);
};

export const useUnifiedAnalyticsContext = (): UnifiedAnalyticsConfig => {
	const context = useContext(UnifiedAnalyticsContext);

	return (
		context ?? {
			productSwitcher: 'appSwitcher',
			cloudId: 'unknown',
			hasSwitcherAnalytics: false,
			hasThirdPartySection: false,
			hasDiscoverySection: false,
		}
	);
};
